export const INDIA_LOCALE = 'en-IN';

export const IST_TIMEZONE = 'Asia/Kolkata';

export const DEFAULT_RISK_PER_TRADE = 25_000;

// Must match round2 and the DB column scale.
export const PRICE_DECIMAL_PLACES = 2;

export const FEEDBACK_BODY_MAX_LENGTH = 2000;

// Display queries only; never the price watcher's feeds or RoI figures.
export const LIST_PAGE_SIZE = 50;

export const NOTIFY_QUEUE = 'notify-deliver';

export const NOTIFICATIONS_PAGE_SIZE = 50;

export const TELEGRAM_QUEUE = 'telegram-deliver';

export const DEFAULT_BOOK = 'general';

export const BOOK_MAX_LENGTH = 32;

/** Separate from DEFAULT_BOOK so chart-filed and hand-filed recommendations never mix. */
export const DEFAULT_CHART_BOOK = 'charts';

export const ANALYST_BIO_MAX_LENGTH = 280;

export const BOOK_DESCRIPTION_MAX_LENGTH = 280;

export const LIMIT_MARGIN_MAX_BPS = { outright: 100, option: 500 } as const;

export const CHANNEL_LABEL_MAX_LENGTH = 64;

export const MEMBER_NAME_MAX_LENGTH = 128;

/** How long a follow invite lets somebody in after it is made. */
export const FOLLOW_INVITE_DAYS = 3;

/** Follow invites an analyst may hold out unused at once; one a person, so a guard, not a quota. */
export const OPEN_FOLLOW_INVITES = 200;

export const PRICE_FRESH_SECONDS = 900;

/**
 * Whitelist: Kite's dump reports `lot_size: 1` for every MCX-FUT row (the order unit, not
 * the contract size). Lots from MCX contract notes (Jul 2026); re-verify at mcxindia.com.
 * One entry per `quoteUnit`: merging SILVER100 (₹/10 g) into SILVER (₹/kg) breaks
 * carryLevels.
 */
interface McxCommodity {
  /** a quoted price is per this many units */
  quoteUnit: number;
  /** lot size in the same units; largest first */
  contracts: Record<string, number>;
}

export const MCX_COMMODITIES: Record<string, McxCommodity> = {
  GOLD: { quoteUnit: 10, contracts: { GOLD: 1000, GOLDM: 100, GOLDTEN: 10 } }, // ₹/10 g
  GOLDGUINEA: { quoteUnit: 8, contracts: { GOLDGUINEA: 8 } }, // ₹/8 g
  GOLDPETAL: { quoteUnit: 1, contracts: { GOLDPETAL: 1 } }, // ₹/g
  SILVER: { quoteUnit: 1, contracts: { SILVER: 30, SILVERM: 5, SILVERMIC: 1 } }, // ₹/kg
  SILVER100: { quoteUnit: 10, contracts: { SILVER100: 100 } }, // ₹/10 g
  COPPER: { quoteUnit: 1, contracts: { COPPER: 2500 } }, // ₹/kg
  CRUDEOIL: { quoteUnit: 1, contracts: { CRUDEOIL: 100, CRUDEOILM: 10 } }, // ₹/bbl
  NATURALGAS: { quoteUnit: 1, contracts: { NATURALGAS: 1250, NATGASMINI: 250 } }, // ₹/mmBtu
};

export const KITE_TOKEN_QUEUE = 'kite-token';

export const KITE_INSTRUMENTS_QUEUE = 'kite-instruments';

// For legs with no recorded tick: a whole rupee is a multiple of every listed tick,
// so a price snapped to it is always accepted.
export const FALLBACK_TICK_SIZE = 1;

// For untraded indices; `toTick` treats this as no grid. Unknown grids use FALLBACK_TICK_SIZE.
export const FINEST_TICK_SIZE = 10 ** -PRICE_DECIMAL_PLACES;

export const LOGIN_STATE_TTL_MS = 15 * 60 * 1000;

export const BROKER_SETTLE_QUEUE = 'broker-settle';

export const BROKER_ARM_QUEUE = 'broker-arm';

export const BROKER_RELEASE_QUEUE = 'broker-release';

export const KITE_APP_QUEUE = 'kite-app';

export const CHANNEL_QUEUE = 'channel-posts';

/** Separate queue, so a failing media fetch never blocks or re-runs the reading. */
export const CHANNEL_ARCHIVE_QUEUE = 'channel-archive';

export const ALERT_QUEUE = 'tradingview-alerts';

export const ALERT_UNFILED_QUEUE = 'tradingview-unfiled';

export const EXTRACT_MAX_TOKENS = 8192;

/** Absolute, not sliding: a polling tab would otherwise never expire. */
export const WEB_SESSION_TTL_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * How long after signing in a person may delete their account. Older, they sign in again first,
 * so a browser left open is not enough to erase somebody.
 */
export const ERASURE_SIGN_IN_WINDOW_MS = 10 * 60 * 1000;

/**
 * PostHog's app host, not the ingest proxy (POSTHOG_API_HOST). The org's region is fixed at
 * signup, so not an env var. Also named in `frame-ancestors`; a wrong region breaks heatmaps.
 */
export const POSTHOG_UI_HOST = 'https://eu.posthog.com';

export const KITE_CALLBACK_PATH = '/kite/callback';

/** Registered in each member's own Kite app; the order tag says whose. */
export const KITE_POSTBACK_PATH = '/kite/postback';

export const TRADINGVIEW_WEBHOOK_PATH = '/tradingview/webhook';

/** Past TradingView retries, short of a chart legitimately repeating itself. */
export const TRADINGVIEW_REDELIVERY_MINUTES = 15;

export const WEB_QUOTE_FLUSH_MS = 400;

export const HTTP_BIND_ADDRESS = '0.0.0.0'; // the container's boundary; compose publishes on 127.0.0.1 only

// Served only by the admin listener, which is unauthenticated and never proxied.
export const HEALTH_PATH = '/health';

export const PNL_SEGMENTS = ['FNO', 'COMMODITY'] as const;

export type PnlSegment = (typeof PNL_SEGMENTS)[number];

export const PNL_LEDGER_QUEUE = 'pnl-ledger';

/**
 * Different measures, never averaged. VERIFIED: Zerodha's realised P&L, net of charges. READ:
 * the desk's own read of Kite — today only the account value, and, on rows before 1 October
 * 2026, a segment's `m2m` gross of charges, kept where the link never stated the day.
 */
export const PNL_SOURCES = ['READ', 'VERIFIED'] as const;

export const VERIFIED_PNL_QUEUE = 'pnl-verified-sync';

/**
 * In trading sessions on the contract's exchange, not calendar days. Ten matches
 * `NEXT_MONTH_DEFAULT_MS` (calendar days).
 */
export const ROLL_WINDOW_SESSIONS = 10;

export const ROLL_QUEUE = 'rollover';

export const EVENT_PROMPT_QUEUE = 'event-prompts';
export const PATH_QUEUE = 'member-path';

export const INDICATED_MARK_QUEUE = 'indicated-mark';

export const INDICATED_SCORE_QUEUE = 'indicated-score';

export const GIFT_MARK_QUEUE = 'indicated-gift';

export const STOCK_MARK_QUEUE = 'indicated-stocks';

export const CALENDAR_SYNC_QUEUE = 'calendar-sync';

export const ECONOMIC_SYNC_QUEUE = 'economic-calendar-sync';

export const STOCK_EVENTS_QUEUE = 'stock-events-sync';

export const MORNING_QUEUE = 'morning-digest';

export const PARTICIPANTS_QUEUE = 'participants';

export const NEWS_RELEASES_QUEUE = 'news-releases';

export const NEWS_READING_QUEUE = 'news-reading';

export const NEWS_WIRE_QUEUE = 'news-wire';

export const NEWS_FILINGS_QUEUE = 'news-filings';

export const NEWS_GOVERNMENT_QUEUE = 'news-government';

export const NEWS_RESULTS_QUEUE = 'news-results';

export const NEWS_EXPECTED_QUEUE = 'news-expected';

export const NEWS_MOVES_QUEUE = 'news-moves';

export const NEWS_FORGET_QUEUE = 'news-forget';

/** AMFI's scheme master, its list of listed companies by size, and expense ratios. */
export const FUND_REFERENCE_QUEUE = 'fund-reference';

/** Which houses' monthly portfolios are due, each sent to be read on its own. */
export const FUND_PORTFOLIOS_QUEUE = 'fund-portfolios';

/** Each member's holdings valued at the night's closes and NAVs, where their Kite was not read. */
export const WORTH_QUEUE = 'portfolio-worth';

/** One house's portfolios for one month. */
export const FUND_HOUSE_QUEUE = 'fund-house';

/** Once the houses asked are read: the companies' industries, and what members' funds did. */
export const FUND_MONTH_QUEUE = 'fund-month';

/** Each id sequence kept past its column's highest value. */
export const SEQUENCES_QUEUE = 'sequences';
